import { BackendModule, Services, ReadCallback, InitOptions } from "i18next";
import createStrapi, { StrapiConfig } from "./strapi";

const supportedLanguages: Record<string, "de-DE" | "en-US"> = {
  "de-DE": "de-DE",
  "en-US": "en-US",
  "en-GB": "en-US",
  de: "de-DE",
  en: "en-US",
};

type BackendOptions = {
  applicationName: string | string[];
  fallbackResource?: Record<string, Record<string, any>>;
  strapiConfig: StrapiConfig;
};

const defaultOptions: BackendOptions = {
  applicationName: "",
  fallbackResource: {
    en: {},
    de: {},
  },
  strapiConfig: {
    limit: 10000,
  },
};

class Backend implements BackendModule<BackendOptions> {
  services: Services;
  options: BackendOptions;
  allOptions: InitOptions;
  type = "backend" as BackendModule["type"];
  static type = "backend" as BackendModule["type"];

  // This constructor actually receives nothing, I noticed that i18n only pass the
  // services, options and allOptions to the init function, so we use the constructor
  // only to initialize the values even tho all of them are undefined.
  constructor(
    services: Services,
    options: BackendOptions,
    allOptions: InitOptions
  ) {
    this.services = services;
    this.options = options;
    this.type = "backend";
    this.allOptions = allOptions;
  }

  init(services: Services, options: BackendOptions, allOptions: InitOptions) {
    if (!options.applicationName) {
      throw new Error("Application name was not provided");
    }

    this.services = services;
    this.options = { ...defaultOptions, ...options };
    this.allOptions = allOptions;
  }

  read(language: string, _: string, callback: ReadCallback) {
    const strapi = createStrapi({
      ...this.options.strapiConfig,
    });

    const fallbackResource = this.options.fallbackResource?.[language] || {};
    const fallbackLanguage = this.getFallbackLanguage("en");
    const contentfulLanguage =
      supportedLanguages[language] ||
      supportedLanguages[fallbackLanguage] ||
      fallbackLanguage;

    strapi
      .getTranslation({
        applicationSlug: this.options.applicationName,
        locale: contentfulLanguage,
        limit: this.options.strapiConfig.limit,
      })
      .then((translations: Record<string, any>) => {
        callback(null, translations);
      })
      .catch((e) => {
        console.error(
          new Error(
            `Could not read translations for language ${language}, will use the fallback resource.`
          )
        );
        console.error(e);

        callback(null, fallbackResource);
      });
  }

  getFallbackLanguage(fallback: string): string {
    const fallbackLng = this.allOptions.fallbackLng;

    if (typeof fallbackLng === "string") return fallbackLng;

    if (Array.isArray(fallbackLng) && fallbackLng.length > 0)
      return fallbackLng[0];

    return fallback;
  }

  create() {}
}

export default Backend;
